import React, { useEffect, useRef, useState } from 'react';
import { Box, Typography, Stack, Chip, Divider, Tooltip } from '@mui/material';
import PauseCircleOutlineIcon from '@mui/icons-material/PauseCircleOutline';
import AddCircleOutlineIcon from '@mui/icons-material/AddCircleOutline';
import RefreshIcon from '@mui/icons-material/Refresh';
import EmojiEventsIcon from '@mui/icons-material/EmojiEvents';
import AccessTimeIcon from '@mui/icons-material/AccessTime';
import HourglassBottomIcon from '@mui/icons-material/HourglassBottom';
import ArrowForwardIcon from '@mui/icons-material/ArrowForward';
import MonetizationOnIcon from '@mui/icons-material/MonetizationOn';
import { ITournamentExtraInformation } from '../../../Core/State/ITournamentExtraInformation';
import { t } from '../../../i18n';

interface TournamentExtraInfoProps {
    tournamentExtraInformation: ITournamentExtraInformation;
    onClose?: () => void;
    inline?: boolean;
    hasStarted?: boolean;
    // Additional props that might come from room state level
    prizePool?: number;
    guaranteedPrizePool?: number;
}

const TournamentExtraInfo: React.FC<TournamentExtraInfoProps> = ({
    tournamentExtraInformation,
    onClose,
    inline = false,
    hasStarted = true,
    prizePool: roomPrizePool,
    guaranteedPrizePool: roomGuaranteedPrizePool
}) => {
    // Use prizePool from props or from tournamentExtraInformation
    const prizePool = roomPrizePool ?? tournamentExtraInformation.prizePool;
    const guaranteedPrizePool = roomGuaranteedPrizePool ?? tournamentExtraInformation.guaranteedPrizePool;
    const containerRef = useRef<HTMLDivElement>(null);
    const [, setTick] = useState(0);
    const isTournamentStarted = hasStarted;

    useEffect(() => {
        if (inline) return;
        const handleClickOutside = (event: MouseEvent) => {
            if (containerRef.current && !containerRef.current.contains(event.target as Node)) {
                onClose?.();
            }
        };
        document.addEventListener('mousedown', handleClickOutside);
        return () => document.removeEventListener('mousedown', handleClickOutside);
    }, [onClose, inline]);

    // Update countdown every second
    useEffect(() => {
        const interval = setInterval(() => setTick(t => t + 1), 1000);
        return () => clearInterval(interval);
    }, []);

    const parseDate = (value?: string | null) => (value ? new Date(value) : null);

    const formatTime = (value?: string | null) => {
        const date = parseDate(value);
        if (!date) return '-';
        return date.toLocaleString('en-GB', { hour: '2-digit', minute: '2-digit', hour12: false });
    };

    const formatCountdown = (value?: string | null) => {
        const target = parseDate(value);
        if (!target) return '';
        const diff = target.getTime() - Date.now();
        if (diff <= 0) return t('GameRoom:tournamentExtraInfo.now');
        const minutes = Math.floor(diff / 60000);
        const seconds = Math.floor((diff % 60000) / 1000);
        if (minutes >= 60) {
            const hours = Math.floor(minutes / 60);
            const mins = minutes % 60;
            return `${hours}h ${mins}m`;
        }
        if (minutes > 0) return `${minutes}m ${seconds}s`;
        return `${seconds}s`;
    };

    const formatChips = (value?: number | null) => {
        if (typeof value !== 'number' || isNaN(value)) return null;
        return value.toLocaleString();
    };

    const formatBlinds = (small?: number | null, big?: number | null) => {
        const smallText = formatChips(small);
        const bigText = formatChips(big);
        if (!smallText || !bigText) return null;
        return `${smallText} / ${bigText}`;
    };

    const finalTableDisabled = Boolean(tournamentExtraInformation.finalTableFeaturesDisabled);
    const breakStatus = tournamentExtraInformation.scheduledBreakStatus || (tournamentExtraInformation.breaksEnabled ? 'upcoming' : 'not_enabled');
    const addonStatus = finalTableDisabled ? 'expired' : (tournamentExtraInformation.addonStatus || (tournamentExtraInformation.addonsEnabled ? 'active' : 'not_enabled'));
    const rebuyStatus = finalTableDisabled ? 'expired' : (tournamentExtraInformation.rebuyStatus || (tournamentExtraInformation.rebuysEnabled ? 'active' : 'not_enabled'));
    const blindLevel = tournamentExtraInformation.blindLevel;
    const currentLevelLabel = typeof blindLevel?.currentLevel === 'number' ? `Level ${blindLevel.currentLevel}` : null;
    const currentBlindsLabel = formatBlinds(blindLevel?.currentSmallBlind, blindLevel?.currentBigBlind);
    const nextBlindsLabel = formatBlinds(blindLevel?.nextSmallBlind, blindLevel?.nextBigBlind);
    const lateRegEnabled = Boolean(tournamentExtraInformation.lateRegistrationEnabled);
    const lateRegStatus = lateRegEnabled
        ? (tournamentExtraInformation.lateRegistrationStatus || 'upcoming')
        : 'not_enabled';
    const rebuyCountdown = isTournamentStarted
        ? (
            rebuyStatus === 'upcoming' && tournamentExtraInformation.rebuyWindowStart
                ? t('GameRoom:tournamentExtraInfo.opensIn', { time: formatCountdown(tournamentExtraInformation.rebuyWindowStart) })
                : rebuyStatus === 'active' && tournamentExtraInformation.rebuyWindowEnd
                ? t('GameRoom:tournamentExtraInfo.closesIn', { time: formatCountdown(tournamentExtraInformation.rebuyWindowEnd) })
                : null
        )
        : null;
    const rebuyInfo =
        !isTournamentStarted && rebuyStatus === 'upcoming' && tournamentExtraInformation.rebuyWindowStart && tournamentExtraInformation.rebuyWindowEnd
            ? t('GameRoom:tournamentExtraInfo.availableAfterStartUntil', { time: formatTime(tournamentExtraInformation.rebuyWindowEnd) })
            : rebuyStatus === 'upcoming' && tournamentExtraInformation.rebuyWindowStart && tournamentExtraInformation.rebuyWindowEnd
            ? t('GameRoom:tournamentExtraInfo.window', { start: formatTime(tournamentExtraInformation.rebuyWindowStart), end: formatTime(tournamentExtraInformation.rebuyWindowEnd) })
            : rebuyStatus === 'expired' && tournamentExtraInformation.rebuyWindowEnd
            ? t('GameRoom:tournamentExtraInfo.closedAt', { time: formatTime(tournamentExtraInformation.rebuyWindowEnd) })
            : null;

    const addonCountdown = isTournamentStarted
        ? (
            addonStatus === 'upcoming' && tournamentExtraInformation.nextAddonPause
                ? t('GameRoom:tournamentExtraInfo.opensIn', { time: formatCountdown(tournamentExtraInformation.nextAddonPause) })
                : addonStatus === 'active' && tournamentExtraInformation.nextAddonEndPause
                ? t('GameRoom:tournamentExtraInfo.closesIn', { time: formatCountdown(tournamentExtraInformation.nextAddonEndPause) })
                : null
        )
        : null;
    const addonInfo =
        !isTournamentStarted && addonStatus === 'upcoming' && tournamentExtraInformation.nextAddonPause
            ? t('GameRoom:tournamentExtraInfo.addonBreakAfterStartAt', { time: formatTime(tournamentExtraInformation.nextAddonPause) })
            : addonStatus === 'active' && tournamentExtraInformation.nextAddonEndPause
            ? t('GameRoom:tournamentExtraInfo.addonBreakClosesAt', { time: formatTime(tournamentExtraInformation.nextAddonEndPause) })
            : addonStatus === 'upcoming' && tournamentExtraInformation.nextAddonPause
            ? t('GameRoom:tournamentExtraInfo.addonBreakAt', { time: formatTime(tournamentExtraInformation.nextAddonPause) })
            : addonStatus === 'expired' && (tournamentExtraInformation.lastAddonBreakAt || tournamentExtraInformation.lastPauseAt)
            ? t('GameRoom:tournamentExtraInfo.endedAt', { time: formatTime(tournamentExtraInformation.lastAddonBreakAt || tournamentExtraInformation.lastPauseAt) })
            : null;

    const getStatusColor = (status: string) => {
        switch (status) {
            case 'active': return '#4caf50';
            case 'upcoming': return '#2196f3';
            case 'expired': return '#666';
            case 'not_enabled': return '#666';
            default: return '#666';
        }
    };

    const getStatusLabel = (status: string) => {
        switch (status) {
            case 'active': return t('GameRoom:tournamentExtraInfo.status.active');
            case 'upcoming': return t('GameRoom:tournamentExtraInfo.status.upcoming');
            case 'expired': return t('GameRoom:tournamentExtraInfo.status.ended');
            case 'not_enabled': return t('GameRoom:tournamentExtraInfo.status.disabled');
            default: return t('GameRoom:tournamentExtraInfo.status.unknown');
        }
    };

    const FeatureRow = ({ icon, title, status, countdown, info, labelOverride }: any) => (
        <Box sx={{
            display: 'flex',
            alignItems: 'flex-start',
            gap: 1.2,
            padding: '7px 10px',
            backgroundColor: 'rgba(0,0,0,0.2)',
            borderRadius: '5px',
            borderLeft: `3px solid ${getStatusColor(status)}`
        }}>
            <Box sx={{ color: getStatusColor(status), mt: '1px', fontSize: '19px' }}>
                {icon}
            </Box>
            <Box sx={{ flex: 1, minWidth: 0 }}>
                <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.6, mb: countdown || info ? 0.3 : 0 }}>
                    <Typography sx={{ fontSize: '13px', fontWeight: 600, color: '#fff' }}>
                        {title}
                    </Typography>
                    <Chip
                        label={labelOverride || getStatusLabel(status)}
                        size="small"
                        sx={{
                            height: '18px',
                            fontSize: '11px',
                            fontWeight: 700,
                            backgroundColor: getStatusColor(status),
                            color: '#fff',
                            '& .MuiChip-label': { px: 0.9, py: 0 }
                        }}
                    />
                </Box>
                {countdown && (
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, mb: info ? 0.25 : 0 }}>
                        <AccessTimeIcon sx={{ fontSize: '13px', color: '#19e5e0' }} />
                        <Typography sx={{ fontSize: '12px', color: '#19e5e0', fontWeight: 600 }}>
                            {countdown}
                        </Typography>
                    </Box>
                )}
                {info && (
                    <Typography sx={{ fontSize: '11px', color: 'rgba(255,255,255,0.5)', lineHeight: 1.3 }}>
                        {info}
                    </Typography>
                )}
            </Box>
        </Box>
    );

    return (
        <Box
            ref={containerRef}
            sx={{
                position: inline ? 'relative' : 'absolute',
                top: inline ? 'auto' : '100%',
                left: inline ? 'auto' : '-10px',
                mt: inline ? 0 : 1,
                p: 1.2,
                backgroundColor: '#1a1b20',
                border: '1px solid #3A3B40',
                borderRadius: '7px',
                minWidth: inline ? '310px' : '336px',
                maxWidth: inline ? '384px' : '360px',
                maxHeight: inline ? '432px' : '384px',
                overflowY: 'auto',
                zIndex: inline ? 'auto' : 9999,
                boxShadow: inline ? 'none' : '0 8px 24px rgba(0, 0, 0, 0.5)',
                color: 'white',
                fontFamily: 'Inter, sans-serif',
                pointerEvents: 'auto'
            }}
        >
            {/* Header */}
            <Box sx={{ mb: 0.9, pb: 0.6, borderBottom: '1px solid #3A3B40' }}>
                <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', gap: 1.2 }}>
                    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.25 }}>
                        <Typography sx={{ fontSize: '13px', fontWeight: 700, color: '#19e5e0', letterSpacing: '0.5px' }}>
                            {t('GameRoom:tournamentExtraInfo.tournamentInfo')}
                        </Typography>
                        {tournamentExtraInformation.tournamentStartTime && (
                            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.4 }}>
                                <AccessTimeIcon sx={{ fontSize: '12px', color: '#19e5e0' }} />
                                <Typography sx={{ fontSize: '11px', color: '#19e5e0', fontWeight: 600 }}>
                                    {isTournamentStarted ? t('GameRoom:tournamentExtraInfo.startedAt', { time: formatTime(tournamentExtraInformation.tournamentStartTime) }) : t('GameRoom:tournamentExtraInfo.startsAt', { time: formatTime(tournamentExtraInformation.tournamentStartTime) })}
                                </Typography>
                            </Box>
                        )}
                    </Box>
                    {(currentLevelLabel || currentBlindsLabel) && (
                        <Box sx={{
                            display: 'flex',
                            flexDirection: 'column',
                            alignItems: 'flex-end',
                            gap: 0.36,
                            backgroundColor: 'rgba(255, 193, 7, 0.1)',
                            padding: '4px 6px',
                            borderRadius: '5px',
                            border: '1px solid rgba(255, 193, 7, 0.3)',
                            minWidth: '115px'
                        }}>
                            <Typography sx={{
                                fontSize: '10px',
                                fontWeight: 700,
                                color: '#FFC107',
                                textTransform: 'uppercase',
                                letterSpacing: '0.3px',
                                lineHeight: 1,
                                textAlign: 'center',
                                width: '100%'
                            }}>
                                {isTournamentStarted && blindLevel?.currentLevel ? t('GameRoom:tournamentExtraInfo.blindLevelNum', { level: blindLevel.currentLevel }) : t('GameRoom:tournamentExtraInfo.blindLevel')}
                            </Typography>
                            {currentBlindsLabel && (
                                <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.6, justifyContent: 'center', width: '100%' }}>
                                    {!isTournamentStarted && (
                                        <Typography sx={{
                                            fontSize: '10px',
                                            fontWeight: 600,
                                            color: 'rgba(255, 224, 130, 0.7)',
                                            lineHeight: 1,
                                            marginRight: '3px'
                                        }}>
                                            {t('GameRoom:tournamentExtraInfo.starting')}
                                        </Typography>
                                    )}
                                    <Typography sx={{
                                        fontSize: '13px',
                                        fontWeight: 700,
                                        color: '#FFE082',
                                        lineHeight: 1
                                    }}>
                                        {currentBlindsLabel}
                                    </Typography>
                                    {nextBlindsLabel && isTournamentStarted && (
                                        <>
                                            <ArrowForwardIcon sx={{ fontSize: '12px', color: 'rgba(255, 193, 7, 0.6)' }} />
                                            <Typography sx={{
                                                fontSize: '12px',
                                                fontWeight: 600,
                                                color: 'rgba(255, 224, 130, 0.7)',
                                                lineHeight: 1
                                            }}>
                                                {nextBlindsLabel}
                                            </Typography>
                                        </>
                                    )}
                                </Box>
                            )}
                        </Box>
                    )}
                </Box>
                {tournamentExtraInformation.isFinalTable && (
                    <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5, mt: 0.36 }}>
                        <EmojiEventsIcon sx={{ fontSize: '13px', color: '#FFC107' }} />
                        <Typography sx={{ fontSize: '11px', color: '#FFC107', fontWeight: 600 }}>
                            {finalTableDisabled ? t('GameRoom:tournamentExtraInfo.finalTableFeaturesDisabled') : t('GameRoom:tournamentExtraInfo.finalTable')}
                        </Typography>
                    </Box>
                )}
            </Box>

            <Stack spacing={0.6}>
                {/* Late Registration */}
                <FeatureRow
                    icon={<HourglassBottomIcon />}
                    title={t('GameRoom:tournamentExtraInfo.lateRegistration')}
                    status={lateRegStatus}
                    countdown={
                        lateRegStatus === 'upcoming' && tournamentExtraInformation.lateRegistrationStart
                            ? t('GameRoom:tournamentExtraInfo.opensIn', { time: formatCountdown(tournamentExtraInformation.lateRegistrationStart) })
                            : lateRegStatus === 'active' && tournamentExtraInformation.lateRegistrationEnd
                            ? t('GameRoom:tournamentExtraInfo.closesIn', { time: formatCountdown(tournamentExtraInformation.lateRegistrationEnd) })
                            : null
                    }
                    info={
                        lateRegStatus === 'active' && tournamentExtraInformation.lateRegistrationEnd
                            ? t('GameRoom:tournamentExtraInfo.until', { time: formatTime(tournamentExtraInformation.lateRegistrationEnd) })
                            : lateRegStatus === 'upcoming' && tournamentExtraInformation.lateRegistrationStart && tournamentExtraInformation.lateRegistrationEnd
                            ? `${formatTime(tournamentExtraInformation.lateRegistrationStart)} - ${formatTime(tournamentExtraInformation.lateRegistrationEnd)}`
                            : lateRegStatus === 'expired' && tournamentExtraInformation.lateRegistrationEnd
                            ? t('GameRoom:tournamentExtraInfo.closedAt', { time: formatTime(tournamentExtraInformation.lateRegistrationEnd) })
                            : null
                    }
                />

                {/* Rebuys */}
                <FeatureRow
                    icon={<RefreshIcon />}
                    title={t('GameRoom:tournamentExtraInfo.rebuys')}
                    status={rebuyStatus}
                    countdown={rebuyCountdown}
                    info={rebuyInfo}
                    labelOverride={!isTournamentStarted && rebuyStatus === 'upcoming' ? t('GameRoom:tournamentExtraInfo.status.enabled') : undefined}
                />

                {/* Add-ons */}
                <FeatureRow
                    icon={<AddCircleOutlineIcon />}
                    title={t('GameRoom:tournamentExtraInfo.addons')}
                    status={addonStatus}
                    countdown={addonCountdown}
                    info={addonInfo}
                    labelOverride={!isTournamentStarted && addonStatus === 'upcoming' ? t('GameRoom:tournamentExtraInfo.status.enabled') : undefined}
                />

                {/* Scheduled Breaks */}
                <FeatureRow
                    icon={<PauseCircleOutlineIcon />}
                    title={t('GameRoom:tournamentExtraInfo.scheduledBreaks')}
                    status={breakStatus}
                    countdown={
                        breakStatus === 'upcoming' && tournamentExtraInformation.nextGamePause
                            ? t('GameRoom:tournamentExtraInfo.nextIn', { time: formatCountdown(tournamentExtraInformation.nextGamePause) })
                            : breakStatus === 'active' && tournamentExtraInformation.nextGameEndPause
                            ? t('GameRoom:tournamentExtraInfo.endsIn', { time: formatCountdown(tournamentExtraInformation.nextGameEndPause) })
                            : null
                    }
                    info={
                        breakStatus === 'upcoming' && tournamentExtraInformation.nextGamePause
                            ? t('GameRoom:tournamentExtraInfo.at', { time: formatTime(tournamentExtraInformation.nextGamePause) })
                            : breakStatus === 'active' && tournamentExtraInformation.nextGameEndPause
                            ? t('GameRoom:tournamentExtraInfo.resumesAt', { time: formatTime(tournamentExtraInformation.nextGameEndPause) })
                            : null
                    }
                />
            </Stack>

            {/* Prize Pool & Stats Section - show when there are rebuys or addons */}
            {((tournamentExtraInformation.rebuyCount !== undefined && tournamentExtraInformation.rebuyCount > 0) ||
              (tournamentExtraInformation.addonCount !== undefined && tournamentExtraInformation.addonCount > 0) ||
              prizePool !== undefined) && (
                <>
                    <Divider sx={{ my: 1, borderColor: '#3A3B40' }} />
                    <Box sx={{
                        padding: '8px 10px',
                        backgroundColor: 'rgba(0,0,0,0.2)',
                        borderRadius: '5px',
                        borderLeft: '3px solid #FFC107'
                    }}>
                        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, mb: 0.8 }}>
                            <MonetizationOnIcon sx={{ fontSize: '19px', color: '#FFC107' }} />
                            <Typography sx={{ fontSize: '13px', fontWeight: 600, color: '#fff' }}>
                                {t('GameRoom:tournamentExtraInfo.stats.title')}
                            </Typography>
                        </Box>

                        {/* Prize Pool */}
                        {prizePool !== undefined && (
                            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 0.5 }}>
                                <Typography sx={{ fontSize: '12px', color: 'rgba(255,255,255,0.6)' }}>
                                    {t('GameRoom:tournamentExtraInfo.stats.prizePool')}
                                </Typography>
                                <Tooltip
                                    title={
                                        (guaranteedPrizePool !== undefined ||
                                         tournamentExtraInformation.collectedFromRegistrations !== undefined ||
                                         tournamentExtraInformation.collectedFromRebuys !== undefined ||
                                         tournamentExtraInformation.collectedFromAddons !== undefined) ? (
                                            <Box sx={{ p: 0.5 }}>
                                                {guaranteedPrizePool !== undefined && (
                                                    <Typography sx={{ fontSize: '11px', color: '#FFC107' }}>
                                                        {t('GameRoom:tournamentExtraInfo.stats.guaranteed')}: ${guaranteedPrizePool.toLocaleString()}
                                                    </Typography>
                                                )}
                                                {tournamentExtraInformation.collectedFromRegistrations !== undefined && (
                                                    <Typography sx={{ fontSize: '11px' }}>
                                                        {t('GameRoom:tournamentExtraInfo.stats.buyIns')}: ${tournamentExtraInformation.collectedFromRegistrations.toLocaleString()}
                                                    </Typography>
                                                )}
                                                {tournamentExtraInformation.collectedFromRebuys !== undefined && tournamentExtraInformation.collectedFromRebuys > 0 && (
                                                    <Typography sx={{ fontSize: '11px' }}>
                                                        {t('GameRoom:tournamentExtraInfo.stats.rebuys')}: ${tournamentExtraInformation.collectedFromRebuys.toLocaleString()}
                                                    </Typography>
                                                )}
                                                {tournamentExtraInformation.collectedFromAddons !== undefined && tournamentExtraInformation.collectedFromAddons > 0 && (
                                                    <Typography sx={{ fontSize: '11px' }}>
                                                        {t('GameRoom:tournamentExtraInfo.stats.addons')}: ${tournamentExtraInformation.collectedFromAddons.toLocaleString()}
                                                    </Typography>
                                                )}
                                            </Box>
                                        ) : ''
                                    }
                                    placement="top"
                                    arrow
                                >
                                    <Typography sx={{
                                        fontSize: '13px',
                                        fontWeight: 600,
                                        color: '#FFC107',
                                        cursor: (guaranteedPrizePool !== undefined ||
                                                 tournamentExtraInformation.collectedFromRegistrations !== undefined ||
                                                 tournamentExtraInformation.collectedFromRebuys !== undefined ||
                                                 tournamentExtraInformation.collectedFromAddons !== undefined) ? 'help' : 'default'
                                    }}>
                                        ${prizePool.toLocaleString()}
                                    </Typography>
                                </Tooltip>
                            </Box>
                        )}

                        {/* Rebuys Count - show when rebuyCount > 0 */}
                        {tournamentExtraInformation.rebuyCount !== undefined && tournamentExtraInformation.rebuyCount > 0 && (
                            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center', mb: 0.5 }}>
                                <Typography sx={{ fontSize: '12px', color: 'rgba(255,255,255,0.6)' }}>
                                    {t('GameRoom:tournamentExtraInfo.stats.rebuysBought')}
                                </Typography>
                                <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                                    <RefreshIcon sx={{ fontSize: '14px', color: '#4caf50' }} />
                                    <Typography sx={{ fontSize: '12px', fontWeight: 600, color: '#4caf50' }}>
                                        {tournamentExtraInformation.rebuyCount}
                                        {tournamentExtraInformation.collectedFromRebuys !== undefined && tournamentExtraInformation.collectedFromRebuys > 0 && (
                                            <span style={{ color: 'rgba(255,255,255,0.5)', fontWeight: 400, marginLeft: '4px' }}>
                                                (${tournamentExtraInformation.collectedFromRebuys.toLocaleString()})
                                            </span>
                                        )}
                                    </Typography>
                                </Box>
                            </Box>
                        )}

                        {/* Addons Count - show when addonCount > 0 */}
                        {tournamentExtraInformation.addonCount !== undefined && tournamentExtraInformation.addonCount > 0 && (
                            <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                                <Typography sx={{ fontSize: '12px', color: 'rgba(255,255,255,0.6)' }}>
                                    {t('GameRoom:tournamentExtraInfo.stats.addonsBought')}
                                </Typography>
                                <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                                    <AddCircleOutlineIcon sx={{ fontSize: '14px', color: '#2196f3' }} />
                                    <Typography sx={{ fontSize: '12px', fontWeight: 600, color: '#2196f3' }}>
                                        {tournamentExtraInformation.addonCount}
                                        {tournamentExtraInformation.collectedFromAddons !== undefined && tournamentExtraInformation.collectedFromAddons > 0 && (
                                            <span style={{ color: 'rgba(255,255,255,0.5)', fontWeight: 400, marginLeft: '4px' }}>
                                                (${tournamentExtraInformation.collectedFromAddons.toLocaleString()})
                                            </span>
                                        )}
                                    </Typography>
                                </Box>
                            </Box>
                        )}
                    </Box>
                </>
            )}
        </Box>
    );
};

export default TournamentExtraInfo;
